import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import api from "../api";

function Remboursements() {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [periode, setPeriode] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // =========================
  // TOKEN
  // =========================

  const token = localStorage.getItem("accessToken");

  // =========================
  // CHARGEMENT
  // =========================

  const loadData = async (status = filter) => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get(
        `/sejours/remboursements?status=${status}`
      );

      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur chargement remboursements :", err);

      setData([]);

      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Impossible de charger les remboursements."
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CHARGEMENT INITIAL
  // =========================

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  // =========================
  // SOCKET / ACTUALISATION
  // =========================
  //
  // Lorsqu'un remboursement est enregistré ailleurs,
  // on recharge automatiquement la liste.
  //
  // =========================

  useEffect(() => {
    const handleRemboursementUpdate = () => {
      loadData(filter);
    };

    window.addEventListener(
      "remboursement_update",
      handleRemboursementUpdate
    );

    return () => {
      window.removeEventListener(
        "remboursement_update",
        handleRemboursementUpdate
      );
    };
  }, [filter]);

  // =========================
  // FILTRE PAR PÉRIODE
  // =========================
  //
  // IMPORTANT :
  //
  // Pour "pending", il ne faut surtout PAS
  // filtrer sur date_remboursement.
  //
  // Un remboursement en attente n'a normalement
  // pas encore de date_remboursement.
  //
  // =========================

  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    // =========================
    // EN ATTENTE
    // =========================
    //
    // L'API a déjà filtré :
    //
    // trop_percu > 0
    // AND trop_percu_rembourse = false
    //
    // On conserve donc toutes les lignes.
    //
    if (filter === "pending") {
      return data;
    }

    // =========================
    // REMBOURSÉS
    // =========================

    return data.filter((s) => {
      if (!s.date_remboursement) {
        return false;
      }

      if (periode === "all") {
        return true;
      }

      const d = new Date(s.date_remboursement);

      if (Number.isNaN(d.getTime())) {
        return false;
      }

      const now = new Date();

      if (periode === "today") {
        return d.toDateString() === now.toDateString();
      }

      if (periode === "month") {
        return (
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear()
        );
      }

      return true;
    });
  }, [data, filter, periode]);

  // =========================
  // TOTAL
  // =========================

  const total = useMemo(() => {
    return filteredData.reduce(
      (sum, s) => sum + Number(s.trop_percu || 0),
      0
    );
  }, [filteredData]);

  // =========================
  // GRAPHIQUE
  // =========================

  const chartData = useMemo(() => {
    if (filter !== "done") {
      return [];
    }

    const grouped = {};

    filteredData.forEach((s) => {
      if (!s.date_remboursement) {
        return;
      }

      const date = new Date(
        s.date_remboursement
      );

      if (Number.isNaN(date.getTime())) {
        return;
      }

      const label = date.toLocaleDateString("fr-FR");

      if (!grouped[label]) {
        grouped[label] = 0;
      }

      grouped[label] += Number(
        s.trop_percu || 0
      );
    });

    return Object.entries(grouped).map(
      ([date, montant]) => ({
        date,
        montant
      })
    );
  }, [filteredData, filter]);

  // =========================
  // MARQUER COMME REMBOURSÉ
  // =========================

  const handleRembourse = async (id) => {
    const confirm = window.confirm(
      "Marquer ce remboursement comme effectué ?"
    );

    if (!confirm) {
      return;
    }

    try {
      setError("");

      await api.post(
        `/sejours/rembourser/${id}`
      );

      alert("Remboursement enregistré.");

      // Recharger immédiatement la liste.
      await loadData("pending");
    } catch (err) {
      console.error(
        "Erreur remboursement :",
        err
      );

      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Erreur lors de l'enregistrement du remboursement.";

      alert(`❌ ${message}`);
    }
  };

  // =========================
  // FORMAT DATE
  // =========================

  const formatDate = (date) => {
    if (!date) {
      return "";
    }

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
      return "";
    }

    return d.toLocaleString("fr-FR");
  };

  // =========================
  // FORMAT MONTANT
  // =========================

  const formatMontant = (montant) => {
    return Number(montant || 0).toLocaleString(
      "fr-FR"
    );
  };

  // =========================
  // RENDER
  // =========================

  return (
    <div className="p-3 md:p-6 max-w-4xl mx-auto space-y-4 md:space-y-6 min-h-[100dvh] bg-gray-100">

      {/* =========================
          HEADER
      ========================= */}

      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-bold">
          Remboursements
        </h2>

        <p className="text-sm text-gray-500 mt-1">
          Gestion des trop-perçus clients
        </p>
      </div>

      {/* =========================
          ERREUR
      ========================= */}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {/* =========================
          FILTRE STATUT
      ========================= */}

      <div className="flex gap-2 justify-center flex-wrap">

        <button
          type="button"
          onClick={() => {
            setPeriode("all");
            setFilter("pending");
          }}
          className={`px-4 py-2 rounded-xl transition ${
            filter === "pending"
              ? "bg-orange-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          En attente
        </button>

        <button
          type="button"
          onClick={() =>
            setFilter("done")
          }
          className={`px-4 py-2 rounded-xl transition ${
            filter === "done"
              ? "bg-green-600 text-white"
              : "bg-white hover:bg-gray-100"
          }`}
        >
          Remboursé
        </button>

      </div>

      {/* =========================
          FILTRE PÉRIODE
          UNIQUEMENT REMBOURSÉS
      ========================= */}

      {filter === "done" && (
        <div className="flex gap-2 justify-center flex-wrap">

          <button
            type="button"
            onClick={() =>
              setPeriode("today")
            }
            className={`px-3 py-2 text-sm rounded-xl transition ${
              periode === "today"
                ? "bg-blue-600 text-white"
                : "bg-white hover:bg-gray-100"
            }`}
          >
            Aujourd’hui
          </button>

          <button
            type="button"
            onClick={() =>
              setPeriode("month")
            }
            className={`px-3 py-2 text-sm rounded-xl transition ${
              periode === "month"
                ? "bg-blue-600 text-white"
                : "bg-white hover:bg-gray-100"
            }`}
          >
            Ce mois
          </button>

          <button
            type="button"
            onClick={() =>
              setPeriode("all")
            }
            className={`px-3 py-2 text-sm rounded-xl transition ${
              periode === "all"
                ? "bg-blue-600 text-white"
                : "bg-white hover:bg-gray-100"
            }`}
          >
            Tout
          </button>

        </div>
      )}

      {/* =========================
          INDICATEUR
      ========================= */}

      <div
        className={`p-4 rounded-2xl text-center shadow ${
          filter === "pending"
            ? "bg-orange-50"
            : "bg-green-50"
        }`}
      >

        <p className="text-sm text-gray-600">
          {filter === "pending"
            ? "À rembourser"
            : "Total remboursé"}
        </p>

        <p className="text-2xl md:text-3xl font-bold">
          ${formatMontant(total)}
        </p>

        <p className="text-xs text-gray-500 mt-1">
          {filteredData.length}{" "}
          {filteredData.length > 1
            ? "remboursements"
            : "remboursement"}
        </p>

      </div>

      {/* =========================
          CHARGEMENT
      ========================= */}

      {loading && (
        <div className="bg-white p-4 rounded-2xl shadow text-center text-gray-500">
          Chargement des remboursements...
        </div>
      )}

      {/* =========================
          GRAPHIQUE
      ========================= */}

      {!loading &&
        filter === "done" &&
        chartData.length > 0 && (
          <div className="bg-white p-4 rounded-2xl shadow">

            <h3 className="font-semibold mb-3">
              Évolution des remboursements
            </h3>

            <ResponsiveContainer
              width="100%"
              height={220}
            >
              <BarChart data={chartData}>
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip
                  formatter={(value) =>
                    `$${formatMontant(value)}`
                  }
                />
                <Bar dataKey="montant" />
              </BarChart>
            </ResponsiveContainer>

          </div>
        )}

      {/* =========================
          LISTE VIDE
      ========================= */}

      {!loading &&
        filteredData.length === 0 && (
          <div className="bg-white p-6 rounded-2xl shadow text-center">

            <p className="text-gray-500">
              {filter === "pending"
                ? "Aucun remboursement en attente."
                : "Aucun remboursement trouvé pour cette période."}
            </p>

          </div>
        )}

      {/* =========================
          LISTE
      ========================= */}

      {!loading &&
        filteredData.length > 0 && (
          <div className="grid gap-3">

            {filteredData.map((s) => (
              <div
                key={s.id}
                className="border p-4 rounded-2xl bg-white shadow-sm"
              >

                {/* CLIENT */}

                <div className="flex justify-between items-start gap-3">

                  <div>
                    <p className="font-semibold text-base md:text-lg break-words">
                      {s.nom || "Client"}{" "}
                      {s.prenom || ""}
                    </p>

                    <p className="text-sm text-gray-500">
                      Séjour #{s.id}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${
                      filter === "pending"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {filter === "pending"
                      ? "En attente"
                      : "Remboursé"}
                  </span>

                </div>

                {/* MONTANT */}

                <p className="text-orange-600 font-bold text-xl mt-3">
                  ${formatMontant(s.trop_percu)}
                </p>

                {/* INFOS REMBOURSEMENT */}

                {filter === "done" && (
                  <div className="mt-3 space-y-1">

                    <p className="text-xs text-gray-500">
                      Remboursé le :{" "}
                      {formatDate(
                        s.date_remboursement
                      )}
                    </p>

                    <p className="text-xs text-gray-500">
                      Par :{" "}
                      {s.user_nom || "—"}
                    </p>

                  </div>
                )}

                {/* ACTION */}

                {filter === "pending" && (
                  <button
                    type="button"
                    onClick={() =>
                      handleRembourse(s.id)
                    }
                    className="mt-3 bg-green-600 hover:bg-green-700 transition text-white px-4 py-2 rounded-xl text-sm"
                  >
                    Remboursé
                  </button>
                )}

              </div>
            ))}

          </div>
        )}

    </div>
  );
}

export default Remboursements;